import { t } from "i18next";
import { AllClaimsState } from "./reducer";
import { ClaimStatusCount } from "../../../../api/claims/claimsInterfaces";
import { BRAZIL_DEALER_LOCATION } from "../../../../utils/constants";
import { isAuthError } from "../../../returns/utils";
import { AllClaimsSortFieldMap } from "../../constants";

const claimStatusMap: { [key: string]: string } = {
  Approved: "completed",
  "In Process": "inProcess",
  "In Progress": "inProgress",
  "Need Action": "needAction",
  "Pending Payment": "pendingPayment",
  Rejected: "rejected",
  Completed: "completed",
  "Approved By All Levels": "approvedByAllLevels",
  "CSR Approved": "csrApproved",
  "Further Approvals Needed": "furtherApprovalsNeeded",
  "On Hold Customer Action": "onHoldCustomerAction",
  "Hold On - Customer Action": "onHoldCustomerAction",
  Open: "open",
  Settled: "settled",
};

export function getClaimLineStatusTranslations(): { [key: string]: string } {
  const base = {
    [t("claims:claims.lineItem.status.approvedByAllLevels")]:
      "approvedByAllLevels",
    [t("claims:claims.lineItem.status.completed")]: "completed",
    [t("claims:claims.lineItem.status.csrApproved")]: "csrApproved",
    [t("claims:claims.lineItem.status.furtherApprovalsNeeded")]:
      "furtherApprovalsNeeded",
    [t("claims:claims.lineItem.status.inProcess")]: "inProcess",
    [t("claims:claims.lineItem.status.inProgress")]: "inProgress",
    [t("claims:claims.lineItem.status.pendingPayment")]: "pendingPayment",
    [t("claims:claims.lineItem.status.needAction")]: "needAction",
    [t("claims:claims.lineItem.status.onHoldCustomerAction")]:
      "onHoldCustomerAction",
    [t("claims:claims.lineItem.status.open")]: "open",
    [t("claims:claims.lineItem.status.rejected")]: "rejected",
    [t("claims:claims.lineItem.status.settled")]: "settled",
  };

  const lookup: { [key: string]: string } = {};
  Object.entries(base).forEach(([key, value]) => {
    lookup[key] = value;
    lookup[key.toLowerCase()] = value;
  });

  return lookup;
}

export function normalizeClaimLineStatus(
  status: string,
  translatedStatusMap: { [key: string]: string }
): string {
  if (!status || status === "-") {
    return "-";
  }

  return (
    claimStatusMap[status] ||
    translatedStatusMap[status] ||
    translatedStatusMap[status.toLowerCase()] ||
    status
  );
}

export function getTabIndex(state: AllClaimsState) {
  return state.tabIndex;
}

export type StatusIconInfo = {
  type: string;
  greyedOut: boolean;
  showFullName: boolean;
};

export function isAuthenticationError(state: AllClaimsState) {
  return isAuthError(state.error);
}

export function hasError(state: AllClaimsState): boolean {
  return !!state.error;
}

export function isLoading(state: AllClaimsState): boolean {
  return state.isLoading;
}

export function isClaimsLoadingForTheFirstTime(state: AllClaimsState): boolean {
  return state.isLoading && !state.isClaimsLoaded;
}

export function isClaimsEnabled(state: AllClaimsState): boolean | undefined {
  return state.featureToggles.isClaimsEnabled;
}

export function getStatusCount(state: AllClaimsState) {
  const stateCount = state.statusCount;
  return {
    allClaims: stateCount.allClaims ?? 0,
    inProcess: stateCount.inProcess ?? 0,
    needAction: stateCount.needAction ?? 0,
    pendingPayment: stateCount.pendingPayment ?? 0,
    rejected: stateCount.rejected ?? 0,
    completed: stateCount.completed ?? 0,
  };
}

export function getStatusByTabIndex(state: AllClaimsState) {
  const statusTabs = getStatusTabs();
  return statusTabs[state.tabIndex];
}

export function getStatusTabs() {
  return [
    "allClaims",
    "inProcess",
    "needAction",
    "pendingPayment",
    "rejected",
    "completed",
  ];
}

export function getAllClaims(state: AllClaimsState) {
  return isLoading(state) ? [] : state.allClaims;
}

export function getFilter(state: AllClaimsState) {
  return state.filter;
}

export function getPaginationDetails(state: AllClaimsState) {
  const { statusCount } = state;
  const statusKey = getStatusByTabIndex(state);
  const totalCount = statusKey
    ? statusCount[statusKey as keyof ClaimStatusCount] ?? 0
    : 0;
  return {
    pageSize: state.pageSize,
    currentPage: state.pageNumber + 1,
    totalCount,
    nextPage: state.pageNumber + 1,
    previousPage: state.pageNumber - 1,
    lastPage: Math.ceil(totalCount / state.pageSize) - 1,
    firstPage: 0,
    showPagination: !!state.allClaims.length && totalCount > 10,
  };
}

export function getTotalAllClaims(state: AllClaimsState) {
  return Object.values(state.statusCount).reduce(
    (sum, count) => sum + count,
    0
  );
}

export function isDownloadDisabled(state: AllClaimsState) {
  return state.downloadDisabled || false;
}

function statusSort(statusA: string, statusB: string) {
  return getStatusTabs().indexOf(statusA) - getStatusTabs().indexOf(statusB);
}

export function getStatusIconInfoList(
  allClaimsStatusList: string[],
  state: AllClaimsState
): StatusIconInfo[] {
  if (!allClaimsStatusList) {
    return [
      {
        type: "inProcess",
        greyedOut: false,
        showFullName: true,
      },
    ];
  }

  const translatedStatusMap = getClaimLineStatusTranslations();
  const normalizedStatusList = allClaimsStatusList.map((status) =>
    normalizeClaimLineStatus(status, translatedStatusMap)
  );

  return [...normalizedStatusList]
    .sort(statusSort)
    .map((claimStatus) => ({
      type: claimStatus,
      greyedOut: shouldGreyOut(state, claimStatus),
      showFullName: normalizedStatusList.length === 1,
    }))
    .sort(statusIconSort);
}

function statusIconSort(
  statusInfoA: StatusIconInfo,
  statusInfoB: StatusIconInfo
) {
  return Number(statusInfoA.greyedOut) - Number(statusInfoB.greyedOut);
}

function shouldGreyOut(state: AllClaimsState, returnStatus: string) {
  const statusTabs = getStatusTabs();
  return state.tabIndex === 0
    ? false
    : statusTabs[state.tabIndex] !== returnStatus;
}

export function getClaimsSortFieldsForApi(state: AllClaimsState) {
  return AllClaimsSortFieldMap.get(state.sortModel[0]?.field);
}

export function getSortModel(state: AllClaimsState) {
  return state.sortModel;
}

export function getSortDirection(state: AllClaimsState) {
  return state.sortModel[0]?.sort?.toUpperCase();
}

export function getPartNumber(state: AllClaimsState): string {
  return state.filter.partNumber || "";
}

export function isBrazilianDealer(state: AllClaimsState): boolean {
  return state.dealerLocation === BRAZIL_DEALER_LOCATION;
}
